import * as Immutable from 'immutable';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

// Utils
import {
    createToolbarList,
    getRequiredToolbarListType,
    isToolbarListTypeAlwaysAvailable,
} from '../utils/toolbarAvailableToolsUtils';
import { ensureSupportingToolbarListsExist } from '../utils/toolbarSupportingTypesUtils';
import { updateToolbarState } from '../currentToolbarActions';

// Types
import { ToolbarItemConfig, ToolbarList, ToolbarListType, ToolbarState } from '../toolbarTypes';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { ImMNCurrentUser } from '../../../user/currentUserSelector';

const useToolbarStateManager = (args: {
    defaultToolbarType: ToolbarListType;
    initialToolbars: ToolbarList[];
    baseToolLists: Record<ToolbarListType, ToolbarItemConfig[]>;
    toolbarTypePriorities: ToolbarListType[];
    selectedElementIds: Immutable.List<string>;
    selectedElements: Immutable.List<ImMNElement>;
    selectedChildElements: Immutable.List<ImMNElement>;
    editorKey: string | undefined;
    hasEditorSelection: boolean;
    permissions: number;
    currentUser: ImMNCurrentUser;
    dispatchGetSelectedParentTaskList: (elementId: string) => ImMNElement | undefined;
}) => {
    const {
        defaultToolbarType,
        initialToolbars,
        toolbarTypePriorities,
        baseToolLists,
        selectedElementIds,
        selectedElements,
        selectedChildElements,
        editorKey,
        hasEditorSelection,
        permissions,
        currentUser,
    } = args;

    const dispatch = useDispatch();

    const [toolbarState, setToolbarState] = useState<ToolbarState>({
        focusIndex: 0,
        toolbars: initialToolbars,
    });

    useEffect(() => {
        const toolbarType = getRequiredToolbarListType(
            defaultToolbarType,
            selectedElements,
            editorKey,
            hasEditorSelection,
        );

        const incomingToolbar = createToolbarList(
            toolbarType,
            toolbarTypePriorities,
            baseToolLists,
            selectedElements,
            selectedChildElements,
            currentUser,
            permissions,
            editorKey,
            hasEditorSelection,
        );

        // If there are no items in the toolbar, don't add it.
        // Exception: if create element / tabs / utils toolbars are always available, add them even if they're empty
        // This is because if you view a read-only board, we want no tools available
        const addIncomingToolbar =
            isToolbarListTypeAlwaysAvailable(incomingToolbar.type) || incomingToolbar.items.length;
        if (!addIncomingToolbar) return;

        setToolbarState((state) => {
            // capture the current state used to create this toolbar
            // this will be used on render, so the toolbar contains the correct state when animating in/out
            incomingToolbar.capturedState = {
                selectedElements,
                selectedElementIds,
                currentUser,
                editorKey,
                hasEditorSelection,
                permissions,
            };

            let existingToolbars = state.toolbars;

            // ensure that if we're adding a toolbar that requires lower priority toolbars, they exist
            // this is to ensure that the "back" button in the toolbar works correctly
            // e.g. if we're adding an "editing" toolbar, we need to ensure that the "selected" toolbar for the same element types exists
            existingToolbars = ensureSupportingToolbarListsExist(
                existingToolbars,
                incomingToolbar,
                toolbarTypePriorities,
                baseToolLists,
                args,
            );

            // replace existing toolbar of the same id, if it exists
            const existingToolbarIndex = existingToolbars.findIndex((t) => t.id === incomingToolbar.id);

            if (existingToolbarIndex !== -1) {
                return {
                    ...state,
                    focusIndex: existingToolbarIndex,
                    toolbars: [...existingToolbars.slice(0, existingToolbarIndex), incomingToolbar],
                };
            }

            // otherwise, add it to the end
            return {
                ...state,
                focusIndex: existingToolbars.length,
                toolbars: [...existingToolbars, incomingToolbar],
            };
        });
    }, [defaultToolbarType, selectedElements, editorKey, hasEditorSelection, permissions]);

    useEffect(() => {
        dispatch(updateToolbarState(toolbarState));
    }, [toolbarState]);
};

export default useToolbarStateManager;
